import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  App,
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import {
  CheckCircleOutlined,
  CloudDownloadOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  FileTextOutlined,
  PlusOutlined,
  ReloadOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import {
  postConciliar,
  postPresignedUrls,
  postSolicitar,
  postVerificar,
  getSolicitudEstado,
  getSolicitudesPorRfc,
  getConciliarStatus,
  deleteSolicitud,
  getCola,
  postCola,
} from '../api';
import type { ReporteListItem, Resumen, ColaItem, Moneda, DesgloseReglas, ConciliarPayload, SolicitudDetalle } from '../api';
import { getCredenciales } from '../api/credenciales';
import type { ClienteCredencial } from '../api/credenciales';
import { cleanEmpresa, formatMXN, pctColor } from '../utils/format';
import { esRfcValido } from '../utils/rfc';
import { useAuth, isAdmin } from '../auth';
import { useEmpresa, EMPRESA_TODAS } from '../empresa';
import { useFiltroFecha } from '../filtroFecha';
import { CargaClientes } from './CargaClientes';
import { ReglasConciliacionModal } from '../components';

const { Title, Paragraph, Text } = Typography;
const { Dragger } = Upload;

// ── Workflow state model ───────────────────────────────────────────

type TrabajoEstado = 'pendiente' | 'solicitado' | 'verificado' | 'conciliando' | 'conciliado' | 'error';

interface TrabajoItem {
  key:             string;
  rfc:             string;
  mes:             string;           // mes de inicio (YYYY-MM), legacy/display
  fecha_inicio:    string;           // YYYY-MM-DD
  fecha_fin:       string;           // YYYY-MM-DD
  estado:          TrabajoEstado;
  solicitudFecha?: string;           // ISO timestamp when Solicitar ran
  solicitudIds?:   string[];
  isSatEnProceso?:   boolean;          // true while esperando que el SAT termine (aun verificando)
  isSolicitarPollActivo?:   boolean;   // true mientras SATSolicitar corre async tras disparar Solicitar
  solicitarPollStarted?:  string;    // timestamp del backend (disparado_en) — mismo valor que usa el marcador de troceo completo
  // Distingue un Solicitar nuevo (la completion del poll reemplaza
  // solicitudIds) de un reintento de una sola ventana (debe sumar a las
  // ventanas ya aceptadas, no pisarlas) — ver poll() de Solicitar más abajo.
  solicitarPollModo?:     'nueva' | 'reintento';
  reintentoDeId?:         string;     // id de la solicitud parcial/error que este reintento reemplaza
  verificarPollIds?:     string[];   // ids que se están consultando tras disparar Verificar (async)
  verificarPollStarted?: string;     // ISO timestamp de cuándo arrancó este ciclo de polling
  verificarStats?: { completadas: number; en_proceso: number; errores: number };
  resumen?:        Resumen;
  excelUrl?:       string;
  errorMsg?:       string;
  fechaCreado:     string;
  jobId?:          string;           // async conciliar job id (while conciliando)
  jobMensaje?:     string;           // current progress message from Lambda
}

function periodoLabel(t: TrabajoItem): string {
  if (t.fecha_inicio && t.fecha_fin) return `${t.fecha_inicio} → ${t.fecha_fin}`;
  return t.mes;
}

const LS_KEY = 'asi_conciliacion_trabajos_v2';
// Debe coincidir con el límite del backend (conciliar_iniciar.py).
const MAX_ARCHIVOS = 25;

function normalizeTrabajo(t: Partial<TrabajoItem> & { key: string; rfc: string; estado: TrabajoEstado; fechaCreado: string; satEnProceso?: boolean }): TrabajoItem {
  let normalizado = t;

  if (normalizado.isSatEnProceso === undefined && normalizado.satEnProceso !== undefined) {
    normalizado = { ...normalizado, isSatEnProceso: normalizado.satEnProceso };
  }

  // Migración: entradas antiguas solo tenían `mes`; derivamos el rango del mes
  if (!normalizado.fecha_inicio || !normalizado.fecha_fin) {
    const mes = normalizado.mes || normalizado.key.split('_')[1] || new Date().toISOString().slice(0, 7);
    const [y, m] = mes.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    normalizado = {
      ...normalizado,
      mes,
      fecha_inicio: `${mes}-01`,
      fecha_fin: `${mes}-${String(lastDay).padStart(2, '0')}`,
    };
  }

  // Un polling de Solicitar que ya superó el timeout de 3 min (ver poll() más
  // abajo) antes de que el usuario recargara la pestaña no debe re-armarse
  // silenciosamente — se limpia acá para no mostrar un aviso de "timeout"
  // engañoso en el primer tick, por algo que el usuario nunca vio arrancar.
  if (normalizado.isSolicitarPollActivo && normalizado.solicitarPollStarted) {
    const startedMs = new Date(normalizado.solicitarPollStarted).getTime();
    const elapsedMin = Number.isFinite(startedMs) ? (Date.now() - startedMs) / 60_000 : Infinity;
    if (elapsedMin > 3) {
      normalizado = {
        ...normalizado,
        isSolicitarPollActivo: undefined,
        solicitarPollStarted: undefined,
        solicitarPollModo: undefined,
      };
    }
  }

  return normalizado as TrabajoItem;
}

function loadTrabajos(): TrabajoItem[] {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.map(normalizeTrabajo) : [];
  }
  catch { return []; }
}
function saveTrabajos(items: TrabajoItem[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(items));
}

// ── Button gate logic ──────────────────────────────────────────────
const ESTADOS_EN_CURSO = new Set(['pendiente', 'en_proceso', 'procesando']);
// Bloquea Solicitar si HAY otro trabajo del mismo RFC con polling activo —
// sin esto, dos trabajos del mismo RFC podían dispararse casi al mismo
// tiempo y el backend (que solo bloquea una vez que la primera sub-ventana
// ya se persistió) no alcanza a rechazar al segundo, cruzando solicitudIds.
// rfcsEnVuelo cubre la ventana entre el click y que la respuesta del POST
// (sync o "en_proceso") se refleje en el estado del trabajo — sin esto, dos
// trabajos del mismo RFC podían dispararse Solicitar casi al mismo tiempo
// porque isSolicitarPollActivo todavía no se había seteado en ninguno.
const canSolicitar = (t: TrabajoItem, todos: TrabajoItem[], rfcsEnVuelo: Set<string>) =>
  (t.estado === 'pendiente' || t.estado === 'error') && !t.isSolicitarPollActivo
  && !rfcsEnVuelo.has(t.rfc)
  && !todos.some(otro => otro.key !== t.key && otro.rfc === t.rfc && otro.isSolicitarPollActivo);
const canVerificar = (t: TrabajoItem) =>
  (t.estado === 'solicitado' || t.estado === 'error')
  && (t.solicitudIds?.length ?? 0) > 0
  && !(t.verificarPollIds && t.verificarPollIds.length > 0);
const canConciliar = (t: TrabajoItem) => t.estado === 'verificado' && !t.jobId;

// ── Helpers ────────────────────────────────────────────────────────
function elapsedLabel(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1)  return 'hace unos segundos';
  if (mins === 1) return 'hace 1 min';
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  return `hace ${hrs}h ${mins % 60}min`;
}

function deleteDescription(t: TrabajoItem): string {
  const label = periodoLabel(t);
  if (t.estado === 'conciliando') {
    return `La entrada de ${t.rfc} · ${label} se eliminará de la vista, pero el proceso sigue corriendo en el servidor — el resultado aparecerá en "Conciliaciones Recientes" cuando termine.`;
  }
  if (t.estado === 'verificado') {
    return `¿Eliminar la entrada de ${t.rfc} · ${label}? Los CFDIs ya descargados permanecen en S3 y se pueden reutilizar.`;
  }
  if (t.solicitudIds?.length) {
    return `¿Eliminar la entrada de ${t.rfc} · ${label}? Se intentará cancelar las solicitudes del SAT en segundo plano.`;
  }
  return `¿Eliminar la entrada de ${t.rfc} · ${label}?`;
}

// ── Page ───────────────────────────────────────────────────────────

export function CargaArchivos() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { rfc: empresaRfc, reportes, refresh: refreshEmpresa } = useEmpresa();
  // Filtro de fecha de mes único -> rango, compartido entre módulos
  // (renombrado acá porque esta pantalla ya usa `rango`/`setRango` para el
  // rango de fechas a SOLICITAR un trabajo nuevo, un concepto distinto del
  // filtro de "Conciliaciones Recientes").
  const { rango: rangoFiltro, setRango: setRangoFiltro } = useFiltroFecha();
  const [activeTab, setActiveTab]       = useState('conciliar');
  const [clientes, setClientes]         = useState<ClienteCredencial[]>([]);
  const [hasClientesCargados, setHasClientesCargados] = useState(false);
  const [canMultiArchivo, setCanMultiArchivo] = useState(false);
  const [trabajos, setTrabajosRaw]      = useState<TrabajoItem[]>(loadTrabajos);
  const [rfcInput, setRfcInput]         = useState('');
  const [rango, setRango]               = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [loadingKey, setLoadingKey]     = useState<Record<string, string>>({});
  const [rfcsSolicitandoEnVuelo, setRfcsSolicitandoEnVuelo] = useState<Set<string>>(new Set());
  const [detalleByRfc, setDetalleByRfc] = useState<Record<string, { loading: boolean; error?: string; solicitudes: SolicitudDetalle[] }>>({});
  const [modalTrabajo, setModalTrabajo] = useState<TrabajoItem | null>(null);
  const [fileList, setFileList]         = useState<UploadFile[]>([]);
  const [monedaPorArchivo, setMonedaPorArchivo] = useState<Record<string, Moneda>>({});
  const [, setTick]                     = useState(0); // force re-render for elapsed time
  const [modalReglas, setModalReglas]   = useState<{
    visible: boolean;
    rfc: string;
    periodo?: string;
    porcentaje: number;
    desglose?: DesgloseReglas;
  }>({ visible: false, rfc: '', porcentaje: 0 });

  // Filtro para Conciliaciones Recientes — el de RFC ahora es el selector
  // global de empresa (Header); el de fecha vive en rangoFiltro (compartido,
  // ver arriba).

  // Ref to avoid stale closure in the polling interval
  const trabajosRef = useRef(trabajos);
  useEffect(() => { trabajosRef.current = trabajos; }, [trabajos]);

  const isMountedRef = useRef(true);
  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);

  const pollingErrorsRef = useRef<Record<string, number>>({});
  const colaLoadedRef = useRef(false);
  const colaAbortRef = useRef<AbortController | null>(null);

  // Cargar cola del backend al montar (fallback a localStorage)
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    getCola(controller.signal)
      .then(cola => {
        if (cancelled) return;
        if (cola && cola.length > 0) {
          const normalizadas = (cola as TrabajoItem[]).map(normalizeTrabajo);
          setTrabajosRaw(normalizadas);
          saveTrabajos(normalizadas);
        }
        colaLoadedRef.current = true;
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('No se pudo cargar la cola del backend:', err);
          colaLoadedRef.current = true;
        }
      });
    return () => { cancelled = true; controller.abort(); };
  }, []);

  // Guardar cola en backend cuando cambia (después de carga inicial)
  useEffect(() => {
    if (!colaLoadedRef.current) return;

    // Cancelar request anterior si existe
    if (colaAbortRef.current) {
      colaAbortRef.current.abort();
    }

    const controller = new AbortController();
    colaAbortRef.current = controller;

    const timeout = setTimeout(() => {
      if (controller.signal.aborted) return;
      postCola(trabajos as ColaItem[], controller.signal)
        .catch(err => {
          if (!controller.signal.aborted) {
            console.warn('No se pudo guardar la cola en el backend:', err);
          }
        })
        .finally(() => {
          if (colaAbortRef.current === controller) {
            colaAbortRef.current = null;
          }
        });
    }, 1000);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [trabajos]);

  useEffect(() => {
    let cancelled = false;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    async function fetchClientes(attempt = 0) {
      try {
        const res = await getCredenciales();
        if (cancelled) return;
        setClientes(res.clientes);
        setHasClientesCargados(true);
        // Si el backend devuelve monedas_soportadas, asumimos que soporta
        // multi-archivo/multi-moneda.
        setCanMultiArchivo(
          res.clientes.some(c => 'monedas_soportadas' in c),
        );
      } catch {
        if (cancelled) return;

        if (attempt < 2) {
          // Reintentar con backoff (2s, 4s) ante errores de red transitorios.
          retryTimeout = setTimeout(() => fetchClientes(attempt + 1), 2000 * (attempt + 1));
        } else {
          // El interceptor de Axios ya mostró el error del último intento;
          // no apilamos otro toast redundante.
          console.warn('getCredenciales exhausted retries');
        }
      }
    }

    fetchClientes();
    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [message]);

  // Refresco simple (sin retry/backoff) para disparar después de crear/editar/
  // borrar un cliente en la pestaña "Carga de Clientes" — evita que el
  // autocomplete de RFC de "Nueva entrada" quede desactualizado hasta que se
  // recargue la página entera.
  const refetchClientes = useCallback(async () => {
    try {
      const res = await getCredenciales();
      setClientes(res.clientes);
      setHasClientesCargados(true);
      setCanMultiArchivo(res.clientes.some(c => 'monedas_soportadas' in c));
    } catch {
      // Falla silenciosa: CargaClientes ya refleja su propio estado igual.
    }
  }, []);

  // Re-render every 30s to update elapsed time labels
  useEffect(() => {
    const id = setInterval(() => setTick(n => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Auto-limpiar cola: cada entrada conciliada se remueve sola a los 10s de
  // terminar, sin esperar a que el resto de la cola también termine. Las que
  // terminaron en error se quedan visibles hasta que el usuario las borre a mano.
  useEffect(() => {
    const hayConciliados = trabajos.some(t => t.estado === 'conciliado');
    if (!hayConciliados) return;
    const timeout = setTimeout(() => {
      setTrabajos(prev => prev.filter(t => t.estado !== 'conciliado'));
      message.info('Fila limpiada automáticamente: las conciliaciones exitosas fueron removidas.');
    }, 10_000);
    return () => clearTimeout(timeout);
  }, [trabajos, message]);

  // Poll active conciliar jobs with setTimeout to avoid overlapping requests
  const activeJobCount = trabajos.filter(t => t.estado === 'conciliando' && t.jobId).length;
  useEffect(() => {
    if (!activeJobCount) return;
    let timeoutId: ReturnType<typeof setTimeout>;

    async function poll() {
      const active = trabajosRef.current.filter(t => t.estado === 'conciliando' && t.jobId);
      if (active.length === 0) return;

      for (const t of active) {
        if (!t.jobId) continue;
        try {
          const status = await getConciliarStatus(t.jobId);
          if (!isMountedRef.current) return;

          // Reset consecutive errors on success
          delete pollingErrorsRef.current[t.jobId];

          if (status.estado === 'COMPLETADO') {
            const report  = status.resultado?.reportes?.[0];
            const resumen = report?.resumen;
            const hasErrors = (status.resultado?.errores?.length ?? 0) > 0;
            patchTrabajo(t.key, {
              estado:     'conciliado',
              resumen,
              excelUrl:   report?.excel_url ?? status.excel_url ?? status.resultado_excel_s3_key ?? undefined,
              jobId:      undefined,
              jobMensaje: undefined,
            });
            refreshEmpresa();
            if (hasErrors) {
              message.warning('Conciliación completada con errores parciales. Revisa el resumen.');
            } else {
              message.success(`Conciliación completa${resumen ? ` — ${Math.round(resumen.porcentaje_conciliado)}% conciliado` : ''}.`);
            }
          } else if (status.estado === 'ERROR') {
            patchTrabajo(t.key, {
              estado:     'error',
              errorMsg:   status.error ?? 'Error en conciliación',
              jobId:      undefined,
              jobMensaje: undefined,
            });
            message.error(status.error ?? 'Error al conciliar. Revisa el detalle en la tabla.');
          } else {
            // PENDIENTE / PROCESANDO — check global 15 min timeout
            const creadoAt = status.creado_at ?? t.fechaCreado;
            const elapsedMin = (Date.now() - new Date(creadoAt).getTime()) / 60_000;
            if (elapsedMin > 15) {
              patchTrabajo(t.key, {
                estado:     'error',
                errorMsg:   'Timeout: el proceso de conciliación excedió los 15 minutos.',
                jobId:      undefined,
                jobMensaje: undefined,
              });
              message.error('Timeout: el proceso de conciliación excedió los 15 minutos.');
              continue;
            }
            patchTrabajo(t.key, { jobMensaje: status.mensaje });
          }
        } catch (err) {
          if (!isMountedRef.current) return;

          const currentErrors = (pollingErrorsRef.current[t.jobId!] || 0) + 1;
          pollingErrorsRef.current[t.jobId!] = currentErrors;

          const is404 = (err as { response?: { status?: number } })?.response?.status === 404;
          if (is404 || currentErrors >= 5) {
            patchTrabajo(t.key, {
              estado:     'error',
              errorMsg:   is404 ? 'Trabajo no encontrado (404).' : 'Error de red al consultar el estado. Reintenta la conciliación.',
              jobId:      undefined,
              jobMensaje: undefined,
            });
            message.error(is404 ? 'Trabajo no encontrado (404).' : 'Error de red al consultar el estado.');
            delete pollingErrorsRef.current[t.jobId!];
          }
        }
      }

      if (isMountedRef.current) {
        timeoutId = setTimeout(poll, 5_000);
      }
    }

    timeoutId = setTimeout(poll, 5_000);
    return () => clearTimeout(timeoutId);
  }, [activeJobCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll active verificar (SATVerificarDescargar corre async desde SATVerificarIniciar)
  const activeVerificarCount = trabajos.filter(t => (t.verificarPollIds?.length ?? 0) > 0).length;
  useEffect(() => {
    if (!activeVerificarCount) return;
    let timeoutId: ReturnType<typeof setTimeout>;
    const TERMINALES = new Set(['completada', 'completada_sin_datos', 'error']);

    async function poll() {
      const active = trabajosRef.current.filter(t => (t.verificarPollIds?.length ?? 0) > 0);

      for (const t of active) {
        if (!t.verificarPollIds) continue;
        try {
          const estados = await Promise.all(t.verificarPollIds.map(getSolicitudEstado));
          if (!isMountedRef.current) return;

          // Tratar como error terminal si el SAT devuelve en_proceso pero con mensaje_error
          const estadosNormalizados = estados.map(e => {
            if (e.estado === 'en_proceso' && e.mensaje_error) {
              return { ...e, estado: 'error' };
            }
            return e;
          });

          if (estadosNormalizados.every(e => TERMINALES.has(e.estado))) {
            const completadas = estadosNormalizados.filter(e => e.estado === 'completada' || e.estado === 'completada_sin_datos').length;
            const errores     = estadosNormalizados.filter(e => e.estado === 'error').length;

            if (completadas === 0 && errores > 0) {
              // Fallo total: el SAT respondió (200 a nivel HTTP/lambda) pero el
              // resultado de negocio es un rechazo/error — no lo disfracemos de
              // "Verificado". Mostramos el motivo real (SAT o excepción).
              const primero = estadosNormalizados.find(e => e.estado === 'error');
              const motivo  = primero?.mensaje_error ?? primero?.mensaje ?? 'El SAT no completó la descarga.';
              patchTrabajo(t.key, {
                estado:               'error',
                isSatEnProceso:         false,
                verificarPollIds:     undefined,
                verificarPollStarted: undefined,
                verificarStats:       { completadas, en_proceso: 0, errores },
                errorMsg:             motivo,
              });
              message.error(`El SAT rechazó/falló la verificación: ${motivo}`);
              continue;
            }

            patchTrabajo(t.key, {
              estado:               'verificado',
              isSatEnProceso:         false,
              verificarPollIds:     undefined,
              verificarPollStarted: undefined,
              verificarStats:       { completadas, en_proceso: 0, errores },
              errorMsg:             undefined,
            });
            if (completadas > 0) {
              message.success(`Descarga completa: ${completadas} paquete(s) de CFDIs listos.`);
            } else {
              message.info('Verificación completa. No se descargaron CFDIs nuevos (puede que ya estuvieran en S3 de una ejecución anterior).');
            }
            continue;
          }

          // Aún en proceso. El SAT puede tardar horas en tener el paquete listo;
          // el worker de fondo (cada 15 min) procesa como máximo
          // BATCH_MAX_SOLICITUDES_POR_INVOCACION filas por corrida (ver
          // verificar_descarga.py) — con muchas sub-solicitudes en cola
          // (rangos largos) puede tardar varias corridas, así que el
          // polling se mantiene activo más tiempo antes de dejar que el
          // usuario reintente Verificar más tarde.
          const started    = t.verificarPollStarted ?? new Date().toISOString();
          const elapsedMin = (Date.now() - new Date(started).getTime()) / 60_000;
          const idsEnVerificacion = t.verificarPollIds?.length ?? 0;
          const timeoutMin = Math.max(3, Math.ceil(idsEnVerificacion / 10) * 15 + 3);
          if (elapsedMin > timeoutMin) {
            patchTrabajo(t.key, {
              isSatEnProceso:         true,
              verificarPollIds:     undefined,
              verificarPollStarted: undefined,
            });
            message.warning(
              idsEnVerificacion > 10
                ? 'El SAT sigue procesando este rango — al ser varias sub-solicitudes, el worker de fondo puede tardar hasta 30-45 min en resolverlas todas. Intenta Verificar más tarde, no hace falta dejar la pestaña abierta.'
                : 'El SAT aún está procesando. Intenta Verificar de nuevo en unos minutos.',
            );
          }
        } catch {
          // Error de red puntual — se reintenta en el siguiente tick.
        }
      }

      if (isMountedRef.current) {
        timeoutId = setTimeout(poll, 4_000);
      }
    }

    timeoutId = setTimeout(poll, 2_000);
    return () => clearTimeout(timeoutId);
  }, [activeVerificarCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll active solicitar (SATSolicitar corre async desde SATSolicitarIniciar).
  // Corta cuando aparece el marcador de troceo completo (ver comentario más
  // abajo, dentro de poll()), no por heurística de conteo estable.
  const activeSolicitarCount = trabajos.filter(t => t.isSolicitarPollActivo).length;
  useEffect(() => {
    if (!activeSolicitarCount) return;
    let timeoutId: ReturnType<typeof setTimeout>;

    async function poll() {
      const active = trabajosRef.current.filter(t => t.isSolicitarPollActivo);

      for (const t of active) {
        const started    = t.solicitarPollStarted ?? new Date().toISOString();
        const startedMs  = new Date(started).getTime();
        // solicitarPollStarted puede venir corrupto del backend (formato no
        // parseable) — sin este guard, elapsedMin da NaN y `NaN > 3` nunca es
        // true, dejando el polling corriendo para siempre en silencio.
        const isStartedValido = Number.isFinite(startedMs);
        const elapsedMin = isStartedValido ? (Date.now() - startedMs) / 60_000 : Infinity;

        let rescateSolicitudIds: string[] | null = null;

        try {
          const result = await getSolicitudesPorRfc(t.rfc);
          if (!isMountedRef.current) return;

          if (result.solicitudes.some(s => !s.fecha_solicitud)) {
            console.warn(`getSolicitudesPorRfc(${t.rfc}): ${result.solicitudes.filter(s => !s.fecha_solicitud).length} sub-solicitud(es) sin fecha_solicitud — quedan fuera de la correlación por timestamp.`);
          }

          // Margen de 5s por drift de reloj navegador/servidor.
          const disparadoEn = t.solicitarPollStarted ? new Date(t.solicitarPollStarted).getTime() - 5_000 : null;
          // Excluye ids ya asignados a OTRO trabajo del mismo RFC (defensa en
          // profundidad — el bloqueo real está en canSolicitar, ver más abajo).
          const idsDeOtrosTrabajos = new Set(
            trabajosRef.current
              .filter(otro => otro.key !== t.key && otro.rfc === t.rfc)
              .flatMap(otro => otro.solicitudIds ?? []));
          const dentroDeEstePedido = result.solicitudes.filter(s =>
            !!s.fecha_solicitud && disparadoEn != null && new Date(s.fecha_solicitud).getTime() >= disparadoEn
            && !idsDeOtrosTrabajos.has(s.id));
          rescateSolicitudIds = dentroDeEstePedido.map(s => s.id);

          // El backend guarda un marcador (troceos_completos) al terminar de
          // crear todas las sub-ventanas — se usa esa señal explícita en vez
          // de adivinar por conteo estable entre ticks (eso podía cortar
          // antes de tiempo si el SAT tardaba más que el intervalo de polling).
          const isTroceoCompleto = (result.troceos_completos ?? []).some(m =>
            disparadoEn != null && new Date(m.fecha_solicitud).getTime() >= disparadoEn);
          if (isTroceoCompleto) {
            const activas = dentroDeEstePedido.filter(s => s.estado === 'pendiente' || s.estado === 'en_proceso');
            const nuevosIds = dentroDeEstePedido.map(s => s.id);
            const isReintento = t.solicitarPollModo === 'reintento';
            const pollFlagsClear = {
              isSolicitarPollActivo: undefined,
              solicitarPollStarted:  undefined,
              solicitarPollModo:     undefined,
            };
            let errorSolicitud: string | null = null;
            if (nuevosIds.length === 0) {
              const marcador = (result.troceos_completos ?? []).find(m =>
                disparadoEn != null && new Date(m.fecha_solicitud).getTime() >= disparadoEn);
              const yaEstabaCompleto = marcador != null && !marcador.con_error;
              // Motivo concreto que dejó el backend en el marcador (ej. RFC sin
              // credenciales FIEL); antes se mostraba un genérico.
              const motivo = marcador?.mensaje_error ?? 'El SAT no aceptó ninguna sub-solicitud.';
              if (!isReintento && !yaEstabaCompleto) errorSolicitud = motivo;
              patchTrabajo(t.key, {
                ...pollFlagsClear,
                ...(isReintento ? {} : yaEstabaCompleto ? {
                  estado:       'verificado' as const,
                  solicitudIds: undefined,
                } : {
                  estado:       'error' as const,
                  solicitudIds: undefined,
                  errorMsg:     motivo,
                }),
              });
            } else if (isReintento) {
              // Reintento de una sola ventana: sumar a las ya aceptadas (menos
              // la vieja que este reintento reemplaza), no reemplazar todo.
              const idViejo = t.reintentoDeId;
              patchTrabajo(t.key, {
                ...pollFlagsClear,
                reintentoDeId: undefined,
                solicitudIds: Array.from(new Set([...(t.solicitudIds ?? []).filter(id => id !== idViejo), ...nuevosIds])),
              });
              if (idViejo) await deleteSolicitud(idViejo).catch(() => {});
            } else {
              patchTrabajo(t.key, {
                ...pollFlagsClear,
                estado:               'solicitado',
                solicitudFecha:       t.solicitudFecha ?? new Date().toISOString(),
                solicitudIds:         nuevosIds,
                isSatEnProceso:       undefined,
                verificarStats:       undefined,
                errorMsg:             undefined,
              });
            }
            if (errorSolicitud) {
              message.error(errorSolicitud);
            } else {
              message.success(
                nuevosIds.length === 0
                  ? 'El SAT no aceptó ninguna sub-solicitud — reintenta Solicitar.'
                  : activas.length === nuevosIds.length
                    ? `SAT: ${nuevosIds.length} solicitud(es) creada(s). Espere unos minutos y luego presione Verificar.`
                    : `SAT: ${nuevosIds.length} solicitud(es) creada(s) (${nuevosIds.length - activas.length} ya resuelta(s) por el proceso automático). Presione Verificar para el resto.`,
              );
            }
            continue;
          }
        } catch {
          // Error de red puntual — se reintenta en el siguiente tick, salvo timeout (ver abajo).
        }

        // Timeout de 3 min (o solicitarPollStarted corrupto, ver arriba) —
        // corre siempre, incluso si getSolicitudesPorRfc falla en cada tick
        // (sin esto, un error de red persistente deja el polling corriendo
        // para siempre, porque el chequeo quedaba dentro del try y el catch
        // vacío lo saltaba).
        if (!isStartedValido) {
          console.error(`Polling de Solicitar: solicitarPollStarted inválido para ${t.key} (rfc ${t.rfc}):`, t.solicitarPollStarted);
        }
        if (elapsedMin > 3) {
          // Si ya vimos sub-solicitudes creadas por este pedido antes de que
          // se cumpliera el timeout, las rescatamos en vez de descartarlas —
          // sin esto el trabajo se queda sin solicitudIds aunque el SAT ya
          // haya creado las ventanas, y Verificar queda inalcanzable.
          const hasRescate = !!rescateSolicitudIds && rescateSolicitudIds.length > 0;
          const isReintento = t.solicitarPollModo === 'reintento';
          const idViejoReintento = isReintento ? t.reintentoDeId : undefined;
          // Solicitar nuevo sin sub-solicitudes ni marcador a los 3 min: no
          // puede quedar "Pendiente" para siempre (típicamente falta FIEL). El
          // reintento de una sola ventana no cambia el estado del trabajo.
          const debeMarcarError = !hasRescate && !isReintento;
          patchTrabajo(t.key, {
            ...(hasRescate
              ? (isReintento
                  ? { solicitudIds: Array.from(new Set([...(t.solicitudIds ?? []).filter(id => id !== idViejoReintento), ...rescateSolicitudIds!])) }
                  : {
                      estado:         'solicitado' as const,
                      solicitudFecha: t.solicitudFecha ?? new Date().toISOString(),
                      solicitudIds:   rescateSolicitudIds!,
                    })
              : debeMarcarError
                ? {
                    estado:   'error' as const,
                    errorMsg: 'No se pudo crear ninguna solicitud. Verifica que el RFC tenga credenciales FIEL cargadas en "Carga de Clientes".',
                  }
                : {}),
            isSolicitarPollActivo:  undefined,
            solicitarPollStarted: undefined,
            solicitarPollModo:    undefined,
            reintentoDeId:        undefined,
          });
          if (hasRescate && idViejoReintento) await deleteSolicitud(idViejoReintento).catch(() => {});
          if (debeMarcarError) {
            message.error('No se pudo crear ninguna solicitud — revisa que el RFC tenga credenciales FIEL cargadas.');
          } else {
            message.warning(
              hasRescate
                ? `El SAT está tardando más de lo esperado, pero se recuperaron ${rescateSolicitudIds!.length} sub-solicitud(es) ya creada(s). Presiona Verificar.`
                : 'El SAT sigue procesando la solicitud. Intenta Solicitar de nuevo en unos minutos.',
            );
          }
        }
      }

      if (isMountedRef.current) {
        timeoutId = setTimeout(poll, 4_000);
      }
    }

    timeoutId = setTimeout(poll, 3_000);
    return () => clearTimeout(timeoutId);
  }, [activeSolicitarCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const rfcOptions = useMemo(() =>
    clientes.map(c => ({
      value: c.rfc,
      label: `${c.rfc}${c.nombre_cliente ? '  —  ' + cleanEmpresa(c.nombre_cliente) : ''}`,
    })),
  [clientes]);

  // Monedas que el cliente de este trabajo puede conciliar. Fallback a
  // MXN/USD/EUR si el cliente no está configurado todavía (no restringe
  // nada para clientes preexistentes).
  const monedasDisponibles = useMemo(() => {
    const cliente = clientes.find(c => c.rfc === modalTrabajo?.rfc);
    return cliente?.monedas_soportadas?.length ? cliente.monedas_soportadas : ['MXN', 'USD', 'EUR'];
  }, [clientes, modalTrabajo]);

  const reportesFiltrados = useMemo(() => {
    const desde = rangoFiltro?.[0] ? rangoFiltro[0].format('YYYY-MM-DD') : null;
    const hasta = rangoFiltro?.[1] ? rangoFiltro[1].format('YYYY-MM-DD') : null;
    return reportes.filter(r => {
      if (empresaRfc !== EMPRESA_TODAS && r.rfc !== empresaRfc) return false;
      // Filtro 100% client-side (mismo dataset que ya trae useEmpresa(), sin
      // llamada nueva al backend) — un reporte "matchea" el rango elegido si
      // su período se superpone con [desde, hasta] en algún punto.
      if (desde && r.periodo?.fin && r.periodo.fin < desde) return false;
      if (hasta && r.periodo?.inicio && r.periodo.inicio > hasta) return false;
      return true;
    });
  }, [reportes, empresaRfc, rangoFiltro]);

  // El backend reemplaza una conciliación del mismo RFC+período al re-correr.
  // Avisamos antes de confirmar. `reportes` ya viene de useEmpresa, no hace
  // falta fetch.
  const conciliacionExistente = useMemo(() => {
    if (!modalTrabajo) return undefined;
    return reportes.find(r =>
      r.rfc === modalTrabajo.rfc
      && r.periodo?.inicio === modalTrabajo.fecha_inicio
      && r.periodo?.fin === modalTrabajo.fecha_fin
    );
  }, [reportes, modalTrabajo]);

  function setTrabajos(fn: (prev: TrabajoItem[]) => TrabajoItem[]) {
    setTrabajosRaw(prev => { const next = fn(prev); saveTrabajos(next); return next; });
  }
  function patchTrabajo(key: string, patch: Partial<TrabajoItem>) {
    setTrabajos(prev => prev.map(t => t.key === key ? { ...t, ...patch } : t));
  }

  // ── Agregar ──
  function agregarTrabajo() {
    const [ini, fin] = rango;
    if (!rfcInput.trim() || !ini || !fin) { message.warning('Ingresa RFC y selecciona un rango de fechas.'); return; }
    const rfc          = rfcInput.trim().toUpperCase();
    // Mismo validador que "Nuevo Cliente" (src/utils/rfc.ts) — sin esto se
    // podía encolar cualquier string y el error recién llegaba del SAT.
    if (!esRfcValido(rfc)) { message.warning('RFC con formato inválido (ej. XAXX010101000).'); return; }
    const fecha_inicio = ini.format('YYYY-MM-DD');
    const fecha_fin    = fin.format('YYYY-MM-DD');
    const mesStr       = ini.format('YYYY-MM');
    const dias           = fin.diff(ini, 'day');
    const maxDias         = 5 * 365 + 2; // ~5 años incluyendo años bisiestos
    const antiguedadDias  = dayjs().diff(ini, 'day');
    if (dias < 0) { message.warning('La fecha final debe ser igual o posterior a la fecha inicial.'); return; }
    if (antiguedadDias > maxDias) { message.warning('El SAT solo permite descargas de hasta 5 años de antigüedad.'); return; }
    if (fin.isAfter(dayjs(), 'day')) { message.warning('La fecha final no puede ser posterior a hoy.'); return; }
    const key = `${rfc}_${fecha_inicio}_${fecha_fin}`;
    if (trabajos.some(t => t.key === key)) { message.info('Ya existe una entrada para ese RFC y rango.'); return; }
    const nuevoTrabajo: TrabajoItem = {
      key, rfc, mes: mesStr, fecha_inicio, fecha_fin,
      estado: 'pendiente', fechaCreado: new Date().toISOString(),
    };
    setTrabajos(prev => [nuevoTrabajo, ...prev]);
    setRfcInput('');
    setRango([null, null]);
    // Si el rango ya está descargado, handleSolicitar lo pasa directo a 'verificado'.
    // Mismo guard que canSolicitar (botón manual) — si hay otro trabajo del
    // mismo RFC en vuelo, se deja 'pendiente' en vez de cruzar solicitudIds.
    if (canSolicitar(nuevoTrabajo, trabajos, rfcsSolicitandoEnVuelo)) {
      handleSolicitar(nuevoTrabajo);
    }
  }

  // ── Solicitar ──
  // Async cuando el rango no cabe en los 29s de API Gateway (ver polling más abajo).
  async function handleSolicitar(t: TrabajoItem) {
    setLoadingKey(prev => ({ ...prev, [t.key]: 'solicitar' }));
    setRfcsSolicitandoEnVuelo(prev => new Set(prev).add(t.rfc));
    try {
      const result = await postSolicitar({
        rfc:          t.rfc,
        fecha_inicio: t.fecha_inicio,
        fecha_fin:    t.fecha_fin,
      });

      if (result.estado === 'en_proceso') {
        patchTrabajo(t.key, {
          isSolicitarPollActivo:  true,
          solicitarPollStarted: result.disparado_en ?? new Date().toISOString(),
          solicitarPollModo:    'nueva',
          errorMsg:             undefined,
        });
        message.info('SAT: solicitud en proceso — actualizando en unos segundos...');
        return;
      }

      const sinNadaQuePedir = result.solicitudes.length === 0 && !result.reutilizadas;
      patchTrabajo(t.key, {
        estado:         sinNadaQuePedir ? 'verificado' : 'solicitado',
        solicitudFecha: new Date().toISOString(),
        solicitudIds:   result.solicitudes.map(s => s.id_solicitud),
        isSatEnProceso:       undefined,
        isSolicitarPollActivo:  undefined,
        solicitarPollStarted: undefined,
        verificarStats: undefined,
        errorMsg:       undefined,
      });
      if (sinNadaQuePedir) {
        message.success(`${t.rfc}: el rango ya estaba descargado — se puede conciliar directo.`);
      } else if (result.reutilizadas) {
        message.warning(
          `${t.rfc}: ya hay solicitudes sin resolver — no se pidió nada nuevo al SAT. Presione Verificar.`,
        );
      } else {
        message.success(`SAT: ${result.solicitudes.length} solicitud(es) enviada(s). Espere unos minutos y luego presione Verificar.`);
      }
    } catch (err) {
      // El interceptor de axios ya muestra un toast con el mensaje real del
      // backend; además lo dejamos en la fila para que el motivo quede visible
      // en la cola (ej. RFC sin credenciales FIEL), no solo en el toast.
      const apiMsg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      patchTrabajo(t.key, { estado: 'error', errorMsg: apiMsg ?? 'Error al solicitar al SAT.' });
    } finally {
      setLoadingKey(prev => { const n = { ...prev }; delete n[t.key]; return n; });
      setRfcsSolicitandoEnVuelo(prev => { const n = new Set(prev); n.delete(t.rfc); return n; });
    }
  }

  // ── Verificar ──
  // POST /verificar solo dispara el trabajo real de forma asíncrona (evita el
  // límite duro de 29s de API Gateway) y devuelve los ids en proceso; el
  // resultado real se obtiene con polling a GET /solicitud/{id} más abajo.
  async function handleVerificar(t: TrabajoItem) {
    setLoadingKey(prev => ({ ...prev, [t.key]: 'verificar' }));
    try {
      const result = await postVerificar(t.solicitudIds);

      if (result.estado === 'sin_pendientes' || result.ids.length === 0) {
        patchTrabajo(t.key, { isSatEnProceso: false, errorMsg: undefined });
        message.info('No hay solicitudes pendientes de verificar.');
        return;
      }

      patchTrabajo(t.key, {
        isSatEnProceso:         true,
        verificarPollIds:     result.ids,
        verificarPollStarted: new Date().toISOString(),
        errorMsg:             undefined,
      });
    } catch {
      // Mismo motivo que en handleSolicitar: el interceptor de axios ya
      // avisa del error HTTP real, no duplicar con un toast genérico acá.
    } finally {
      setLoadingKey(prev => { const n = { ...prev }; delete n[t.key]; return n; });
    }
  }

  // ── Detalle por sub-solicitud (ventana × tipo) ──
  async function fetchDetalle(rfc: string) {
    setDetalleByRfc(prev => ({ ...prev, [rfc]: { loading: true, solicitudes: prev[rfc]?.solicitudes ?? [] } }));
    try {
      const result = await getSolicitudesPorRfc(rfc);
      setDetalleByRfc(prev => ({ ...prev, [rfc]: { loading: false, solicitudes: result.solicitudes } }));
    } catch {
      setDetalleByRfc(prev => ({ ...prev, [rfc]: { loading: false, error: 'No se pudo cargar el detalle.', solicitudes: prev[rfc]?.solicitudes ?? [] } }));
    }
  }

  // Reintenta una ventana en error o una 'completada' parcial, con /solicitar
  // — ya viene deshabilitado si hay otra solicitud en curso para el RFC.
  async function handleReintentarVentana(t: TrabajoItem, fila: SolicitudDetalle) {
    if (!fila.fecha_inicio || !fila.fecha_fin) return;
    const loadingKeyVentana = `${t.key}_reintentar_${fila.id}`;
    setLoadingKey(prev => ({ ...prev, [loadingKeyVentana]: 'reintentar' }));
    setRfcsSolicitandoEnVuelo(prev => new Set(prev).add(t.rfc));
    try {
      // Una parcial ya está 'completada' — reverificar no hace nada, hace falta una solicitud nueva.
      if (RECHAZOS_SAT_TERMINALES.has(fila.estado_sat ?? '') || fila.parcial) {
        const result = await postSolicitar({
          rfc:          t.rfc,
          fecha_inicio: fila.fecha_inicio,
          fecha_fin:    fila.fecha_fin,
          tipos:        [fila.tipo],
        });
        if (result.estado === 'en_proceso') {
          // Rango largo — se reusa el mismo mecanismo de polling que Solicitar,
          // pero en modo 'reintento' (suma esta ventana en vez de reemplazar
          // las ya aceptadas, ver poll() más arriba).
          patchTrabajo(t.key, {
            isSolicitarPollActivo:  true,
            solicitarPollStarted: result.disparado_en ?? new Date().toISOString(),
            solicitarPollModo:    'reintento',
            reintentoDeId:        fila.id,
          });
          message.info(`Ventana ${fila.fecha_inicio} → ${fila.fecha_fin}: SAT en proceso — actualizando en unos segundos...`);
        } else {
          patchTrabajo(t.key, {
            solicitudIds: [...(t.solicitudIds ?? []).filter(id => id !== fila.id), ...result.solicitudes.map(s => s.id_solicitud)],
          });
          if (result.reutilizadas) {
            message.warning(`Ventana ${fila.fecha_inicio} → ${fila.fecha_fin}: ya hay solicitudes sin resolver para ${t.rfc} — no se reenvió al SAT.`);
          } else {
            message.success(`Ventana ${fila.fecha_inicio} → ${fila.fecha_fin} reenviada al SAT.`);
            // Cancela la fila vieja para que el resumen no la sume dos veces.
            await deleteSolicitud(fila.id).catch(() => {});
          }
        }
      } else {
        // El SAT ya había aceptado esta solicitud — reverificar el mismo id en vez
        // de gastar una solicitud nueva (evita duplicar solicitudes al SAT).
        await postVerificar([fila.id]);
        patchTrabajo(t.key, {
          verificarPollIds:     Array.from(new Set([...(t.verificarPollIds ?? []), fila.id])),
          verificarPollStarted: undefined,
        });
        message.success(`Reverificando ventana ${fila.fecha_inicio} → ${fila.fecha_fin} (el SAT ya la había aceptado).`);
      }
      await fetchDetalle(t.rfc);
    } catch {
      // El interceptor de axios ya muestra el error real del backend.
    } finally {
      setLoadingKey(prev => { const n = { ...prev }; delete n[loadingKeyVentana]; return n; });
      setRfcsSolicitandoEnVuelo(prev => { const n = new Set(prev); n.delete(t.rfc); return n; });
    }
  }

  // ── Conciliar ──
  // Los extractos se suben directo a S3 con URLs firmadas (en vez de viajar
  // como base64 dentro del body de /conciliar) para evitar el límite de
  // ~10 MB de API Gateway/CloudFront en cargas con varios PDFs pesados.
  async function handleConciliar(t: TrabajoItem) {
    const entradas = fileList
      .map(f => ({ file: f.originFileObj as File | undefined, uid: f.uid }))
      .filter((e): e is { file: File; uid: string } => !!e.file);
    if (entradas.length === 0) { message.warning('Selecciona al menos un extracto bancario.'); return; }

    setLoadingKey(prev => ({ ...prev, [t.key]: 'conciliar' }));

    try {
      const { archivos: presignedArchivos } = await postPresignedUrls({
        rfc: t.rfc,
        archivos: entradas.map(({ file, uid }) => ({
          nombre:       file.name,
          tipo_archivo: fileExt(file.name),
          moneda:       monedaPorArchivo[uid] ?? monedasDisponibles[0],
        })),
      });

      if (presignedArchivos.length !== entradas.length) {
        throw new Error('Respuesta inesperada al solicitar URLs de carga.');
      }

      await Promise.all(presignedArchivos.map(async (p, i) => {
        const res = await fetch(p.upload_url, { method: 'PUT', body: entradas[i].file });
        if (!res.ok) throw new Error(`No se pudo subir "${p.nombre}" a S3 (HTTP ${res.status}).`);
      }));

      const payload: ConciliarPayload = {
        rfc:          t.rfc,
        mes:          t.mes,
        fecha_inicio: t.fecha_inicio,
        fecha_fin:    t.fecha_fin,
        archivos: presignedArchivos.map(p => ({
          s3_key:       p.s3_key,
          tipo_archivo: p.tipo_archivo,
          moneda:       p.moneda,
        })),
      };

      const { job_id } = await postConciliar(payload);
      patchTrabajo(t.key, { estado: 'conciliando', jobId: job_id, jobMensaje: 'En fila de conciliación…' });
      setModalTrabajo(null);
      setFileList([]);
      setMonedaPorArchivo({});
      message.info('Conciliación iniciada. El proceso puede tardar varios minutos — el estado se actualiza automáticamente.');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al iniciar la conciliación.');
    } finally {
      setLoadingKey(prev => { const n = { ...prev }; delete n[t.key]; return n; });
    }
  }

  // ── Columns ──
  const columns: ColumnsType<TrabajoItem> = [
    {
      title: 'RFC',
      key: 'rfc',
      width: 160,
      render: (_, t) => <Tag style={{ fontFamily: 'monospace' }}>{t.rfc}</Tag>,
    },
    {
      title: 'Periodo',
      key: 'periodo',
      width: 180,
      render: (_, t) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{periodoLabel(t)}</Text>,
    },
    {
      title: 'Estado SAT',
      key: 'estado',
      width: 200,
      render: (_, t) => {
        if (t.estado === 'pendiente') {
          return <Tag color="default">Pendiente</Tag>;
        }
        if (t.estado === 'error') {
          const msg = t.errorMsg ?? 'Error';
          return (
            <Tag color="error" icon={<WarningOutlined />} title={msg} style={{ maxWidth: 190, whiteSpace: 'normal' }}>
              {msg}
            </Tag>
          );
        }
        if (t.estado === 'solicitado') {
          if (t.isSatEnProceso) {
            return (
              <Space direction="vertical" size={2}>
                <Tag color="warning" icon={<SyncOutlined spin />}>SAT procesando…</Tag>
                {t.solicitudFecha && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    Solicitado {elapsedLabel(t.solicitudFecha)}
                  </Text>
                )}
              </Space>
            );
          }
          return (
            <Space direction="vertical" size={2}>
              <Tag color="processing" icon={<SyncOutlined spin />}>Esperando SAT…</Tag>
              {t.solicitudFecha && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  <ClockCircleOutlined style={{ marginRight: 4 }} />
                  {elapsedLabel(t.solicitudFecha)}
                </Text>
              )}
            </Space>
          );
        }
        if (t.estado === 'verificado') {
          const count = t.verificarStats?.completadas ?? 0;
          return (
            <Space direction="vertical" size={2}>
              <Tag color="blue" icon={<CheckCircleOutlined />}>
                {count > 0 ? `${count} paquete(s) listos` : 'Verificado'}
              </Tag>
              {count === 0 && (
                <Text type="secondary" style={{ fontSize: 11 }}>Sin CFDIs nuevos descargados</Text>
              )}
            </Space>
          );
        }
        if (t.estado === 'conciliando') {
          return (
            <Space direction="vertical" size={2}>
              <Tag color="processing" icon={<SyncOutlined spin />}>Conciliando…</Tag>
              {t.jobMensaje && (
                <Text type="secondary" style={{ fontSize: 11 }}>{t.jobMensaje}</Text>
              )}
            </Space>
          );
        }
        if (t.estado === 'conciliado') {
          return (
            <Space direction="vertical" size={2}>
              <Tag color="success" icon={<CheckCircleOutlined />}>Conciliado</Tag>
              {t.resumen && (
                <Text style={{ fontSize: 11, color: pctColor(t.resumen.porcentaje_conciliado) }}>
                  {Math.round(t.resumen.porcentaje_conciliado)}% conciliado
                </Text>
              )}
            </Space>
          );
        }
        return null;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, t) => (
        <Space size={4} wrap>
          {/* Step 1 */}
          <Button
            size="small"
            icon={<CloudDownloadOutlined />}
            disabled={!canSolicitar(t, trabajos, rfcsSolicitandoEnVuelo)}
            loading={loadingKey[t.key] === 'solicitar' || !!t.isSolicitarPollActivo}
            onClick={() => handleSolicitar(t)}
          >
            1. Solicitar
          </Button>

          {/* Step 2 */}
          <Button
            size="small"
            icon={<SyncOutlined />}
            disabled={!canVerificar(t)}
            loading={loadingKey[t.key] === 'verificar' || (t.verificarPollIds?.length ?? 0) > 0}
            onClick={() => handleVerificar(t)}
          >
            2. Verificar
          </Button>

          {/* Step 3 */}
          <Button
            size="small"
            type={canConciliar(t) ? 'primary' : 'default'}
            icon={<ThunderboltOutlined />}
            disabled={!canConciliar(t) || !hasClientesCargados || clientes.length === 0}
            loading={loadingKey[t.key] === 'conciliar'}
            style={canConciliar(t) ? { background: '#6f42c1', borderColor: '#6f42c1' } : {}}
            onClick={() => { setModalTrabajo(t); setFileList([]); }}
          >
            {t.estado === 'conciliado' ? 'Conciliado' : '3. Conciliar'}
          </Button>

          {t.excelUrl && (
            <Button size="small" href={t.excelUrl} target="_blank" rel="noopener noreferrer">
              Excel
            </Button>
          )}

          <Popconfirm
            title="Eliminar entrada"
            description={deleteDescription(t)}
            onConfirm={async () => {
              setLoadingKey(prev => ({ ...prev, [t.key]: 'eliminar' }));
              try {
                setTrabajos(prev => prev.filter(x => x.key !== t.key));
                if (t.estado === 'conciliando') {
                  message.info('Entrada eliminada de la vista. El proceso sigue corriendo en el servidor — el resultado aparecerá en "Conciliaciones Recientes" cuando termine.', 6);
                } else {
                  message.info('Entrada eliminada.');
                }

                // Intentar cancelar solicitudes SAT en background (best-effort)
                if (t.solicitudIds?.length && t.estado !== 'verificado') {
                  try {
                    await Promise.all(t.solicitudIds.map(id => deleteSolicitud(id)));
                  } catch (e) {
                    const err = e as { response?: { data?: { error?: string } } };
                    message.warning(
                      'La entrada se eliminó, pero no se pudieron cancelar las solicitudes del SAT: ' +
                      (err?.response?.data?.error || 'Error desconocido')
                    );
                  }
                }
              } finally {
                setLoadingKey(prev => { const n = { ...prev }; delete n[t.key]; return n; });
              }
            }}
            okText="Eliminar"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={loadingKey[t.key] === 'eliminar'}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const admin = isAdmin(user);

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>Carga de Archivos</Title>

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 20 }}>
        <Tabs.TabPane tab="Conciliar" key="conciliar">

      {/* ── Nueva entrada ── */}
      <Card style={{ marginBottom: 20 }}>
        <Row gutter={12} align="middle" wrap={false}>
          <Col flex="auto">
            <AutoComplete
              style={{ width: '100%' }}
              placeholder="RFC (ej. XAXX010101000)"
              value={rfcInput}
              onChange={v => setRfcInput(v.toUpperCase())}
              options={rfcOptions}
              filterOption={(input, opt) =>
                opt!.value.toLowerCase().includes(input.toLowerCase()) ||
                opt!.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Col>
          <Col flex="280px">
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={['Inicio', 'Fin']}
              value={rango}
              onChange={dates => setRango(dates || [null, null])}
              format="YYYY-MM-DD"
              disabledDate={current => !!current && current.isAfter(dayjs(), 'day')}
              inputReadOnly
            />
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={agregarTrabajo}>
              Agregar
            </Button>
          </Col>
        </Row>
      </Card>

      {/* ── Cola ── */}
      <Card
        title="Fila de Conciliación"
        styles={{ body: { padding: 0 } }}
        style={{ marginBottom: 28 }}
        extra={
          trabajos.length > 0 && (
            <Button size="small" danger onClick={() => setTrabajos(() => [])}>
              Limpiar
            </Button>
          )
        }
      >
        {trabajos.length === 0
          ? <Empty description="Agregue un RFC y rango de fechas para comenzar." style={{ padding: 32 }} />
          : (
            <Table<TrabajoItem>
              rowKey="key"
              columns={columns}
              dataSource={trabajos}
              pagination={false}
              size="small"
              scroll={{ x: 780 }}
              expandable={{
                expandedRowRender: t => {
                  const hasSubSolicitudes = (t.solicitudIds?.length ?? 0) > 0;
                  // detalleByRfc trae TODAS las sub-solicitudes históricas del RFC —
                  // se filtra a las de esta tarjeta (mismo rango) para no mezclar
                  // otras tarjetas del mismo RFC con periodos distintos.
                  const detalleRfc = detalleByRfc[t.rfc];
                  const detalle = detalleRfc && {
                    ...detalleRfc,
                    solicitudes: detalleRfc.solicitudes.filter(s => t.solicitudIds?.includes(s.id)),
                  };
                  return (
                    <>
                      {t.estado === 'conciliando' && (
                        <Alert
                          type="info"
                          icon={<SyncOutlined spin />}
                          showIcon
                          message="Conciliación en proceso"
                          description={t.jobMensaje ?? 'El servidor está procesando el extracto bancario. Actualización automática cada 5 segundos.'}
                          style={{ margin: '8px 24px' }}
                        />
                      )}
                      {t.estado === 'solicitado' && t.isSatEnProceso && (
                        <Alert
                          type="warning"
                          icon={<SyncOutlined spin />}
                          showIcon
                          message="El SAT aún está preparando los paquetes"
                          description="Esto puede tardar entre 2 y 15 minutos. Presiona Verificar de nuevo cuando haya pasado ese tiempo."
                          style={{ margin: '8px 24px' }}
                        />
                      )}
                      {t.resumen && <ResumenExpandido resumen={t.resumen} />}
                      {t.errorMsg && (
                        <Text type="danger" style={{ padding: '8px 24px', display: 'block' }}>{t.errorMsg}</Text>
                      )}
                      {hasSubSolicitudes && (
                        <SolicitudesDetalleTable
                          detalle={detalle}
                          bloqueado={!!detalle?.solicitudes.some(s => ESTADOS_EN_CURSO.has(s.estado))}
                          onReintentar={fila => handleReintentarVentana(t, fila)}
                          isRetrying={id => loadingKey[`${t.key}_reintentar_${id}`] === 'reintentar'}
                        />
                      )}
                    </>
                  );
                },
                onExpand: (expanded, t) => {
                  if (expanded && (t.solicitudIds?.length ?? 0) > 0) fetchDetalle(t.rfc);
                },
                rowExpandable: t =>
                  !!(t.resumen || t.errorMsg || (t.estado === 'solicitado' && t.isSatEnProceso) || t.estado === 'conciliando' || (t.solicitudIds?.length ?? 0) > 0),
                defaultExpandAllRows: false,
              }}
            />
          )
        }
      </Card>

      {/* ── Conciliaciones recientes ── */}
      <Divider orientation="left">Conciliaciones Recientes</Divider>
      <Space wrap style={{ marginBottom: 12 }}>
        <DatePicker.RangePicker
          value={rangoFiltro ?? [null, null]}
          onChange={v => setRangoFiltro(v ? [v[0], v[1]] : null)}
          format="YYYY-MM-DD"
          placeholder={['Desde', 'Hasta']}
        />
        {rangoFiltro && (
          <Button type="link" onClick={() => setRangoFiltro(null)}>
            Limpiar Filtro
          </Button>
        )}
        <Text type="secondary">{reportesFiltrados.length} reportes{empresaRfc !== EMPRESA_TODAS ? ` de ${empresaRfc}` : ''}</Text>
      </Space>
      <ReportesTable
        reportes={reportesFiltrados}
        onOpenReglas={(r) =>
          setModalReglas({
            visible: true,
            rfc: r.rfc,
            periodo: r.periodo?.inicio
              ? `${r.periodo.inicio} → ${r.periodo.fin}`
              : undefined,
            porcentaje: r.resumen.porcentaje_conciliado || 0,
            desglose: r.desglose_reglas,
          })
        }
      />

      {/* ── Modal conciliar ── */}
      <Modal
        open={!!modalTrabajo}
        title={
          modalTrabajo
            ? <span>Conciliar — <Tag style={{ fontFamily: 'monospace' }}>{modalTrabajo.rfc}</Tag> · {periodoLabel(modalTrabajo)}</span>
            : ''
        }
        onCancel={() => { setModalTrabajo(null); setFileList([]); setMonedaPorArchivo({}); }}
        footer={[
          <Button key="cancel" onClick={() => { setModalTrabajo(null); setFileList([]); setMonedaPorArchivo({}); }}>Cancelar</Button>,
          <Button
            key="run"
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={modalTrabajo ? loadingKey[modalTrabajo.key] === 'conciliar' : false}
            style={{ background: '#6f42c1', borderColor: '#6f42c1' }}
            onClick={() => modalTrabajo && handleConciliar(modalTrabajo)}
          >
            Ejecutar Conciliación
          </Button>,
        ]}
      >
        {conciliacionExistente && (
          <Alert
            type="warning"
            message="Ya existe una conciliación para este RFC y período. Al ejecutar, se reemplazará."
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        {modalTrabajo?.verificarStats && modalTrabajo.verificarStats.completadas > 0 && (
          <Alert
            type="success"
            message={`${modalTrabajo.verificarStats.completadas} paquete(s) de CFDIs descargados del SAT y listos para conciliar.`}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        {modalTrabajo?.verificarStats && modalTrabajo.verificarStats.completadas === 0 && (
          <Alert
            type="info"
            message="No se descargaron CFDIs nuevos en esta sesión. La conciliación usará los CFDIs que ya estén en S3 de ejecuciones anteriores."
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {canMultiArchivo
            ? 'Suba uno o varios extractos bancarios (puede combinar distintos bancos y monedas). Indique la moneda de cada archivo — el algoritmo detecta el banco automáticamente y concilia todo junto contra los CFDIs del SAT, usando la moneda real de cada factura para el match.'
            : 'Suba un extracto bancario. Indique la moneda del archivo — el algoritmo detecta el banco automáticamente y concilia contra los CFDIs del SAT, usando la moneda real de cada factura para el match.'}
        </Paragraph>
        <Dragger
          fileList={fileList}
          showUploadList={false}
          beforeUpload={() => false}
          onChange={({ fileList: fl }) => {
            if (fl.length > MAX_ARCHIVOS) {
              message.warning(
                `Solo se pueden subir ${MAX_ARCHIVOS} archivos a la vez. Se descartaron ${fl.length - MAX_ARCHIVOS}.`
              );
            }
            const trimmed = fl.slice(-MAX_ARCHIVOS);
            setFileList(trimmed);
            setMonedaPorArchivo(prev => {
              const next: Record<string, Moneda> = {};
              trimmed.forEach(f => { next[f.uid] = prev[f.uid] ?? monedasDisponibles[0]; });
              return next;
            });
          }}
          accept=".xlsx,.csv,.pdf,.txt,.mt940"
          multiple={canMultiArchivo}
          maxCount={MAX_ARCHIVOS}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 32, color: '#6f42c1' }} />
          </p>
          <p className="ant-upload-text">Arrastre los extractos bancarios aquí o haga clic</p>
          <p className="ant-upload-hint">
            {canMultiArchivo
              ? 'xlsx · csv · pdf · txt · mt940'
              : 'xlsx · csv · pdf · txt · mt940 · 1 archivo'}
          </p>
        </Dragger>
        {fileList.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {fileList.map(f => (
              <Row key={f.uid} align="middle" gutter={8} style={{ marginBottom: 8 }}>
                <Col flex="auto">
                  <Text ellipsis style={{ display: 'block' }}>{f.name}</Text>
                </Col>
                <Col>
                  <Select
                    size="small"
                    value={monedaPorArchivo[f.uid] ?? monedasDisponibles[0]}
                    onChange={(v: Moneda) => setMonedaPorArchivo(prev => ({ ...prev, [f.uid]: v }))}
                    style={{ width: 90 }}
                    options={monedasDisponibles.map(m => ({ value: m, label: m }))}
                  />
                </Col>
                <Col>
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setFileList(fl => fl.filter(x => x.uid !== f.uid));
                      setMonedaPorArchivo(prev => { const n = { ...prev }; delete n[f.uid]; return n; });
                    }}
                  />
                </Col>
              </Row>
            ))}
          </div>
        )}
      </Modal>
        </Tabs.TabPane>
        {admin && (
          <Tabs.TabPane tab="Carga de Clientes" key="clientes">
            <CargaClientes onClienteChange={refetchClientes} />
          </Tabs.TabPane>
        )}
      </Tabs>

      <ReglasConciliacionModal
        visible={modalReglas.visible}
        onClose={() => setModalReglas(prev => ({ ...prev, visible: false }))}
        rfc={modalReglas.rfc}
        periodo={modalReglas.periodo}
        porcentaje={modalReglas.porcentaje}
        desglose={modalReglas.desglose}
      />
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

interface ResumenExpandidoProps {
  resumen: Resumen;
}

function ResumenExpandido({ resumen }: ResumenExpandidoProps) {
  return (
    <Row gutter={24} style={{ padding: '12px 24px', background: '#fafafa' }}>
      <Col>
        <Statistic
          title="% Conciliado"
          value={Math.round(resumen.porcentaje_conciliado)}
          suffix="%"
          valueStyle={{ color: pctColor(resumen.porcentaje_conciliado), fontSize: 20 }}
        />
      </Col>
      <Col><Statistic title="Conciliados" value={resumen.matched} /></Col>
      {!!resumen.matched_ia && <Col><Statistic title="Conciliados IA" value={resumen.matched_ia} /></Col>}
      <Col>
        <Statistic
          title="Sin CFDI"
          value={resumen.sin_cfdi}
          valueStyle={{ color: resumen.sin_cfdi > 0 ? '#dc3545' : undefined, fontSize: 20 }}
        />
      </Col>
      <Col><Statistic title="Sin Banco" value={resumen.sin_movimiento_banco} /></Col>
      <Col><Statistic title="Total Banco" value={formatMXN(resumen.monto_total_banco)} /></Col>
      <Col><Statistic title="Diferencia" value={formatMXN(resumen.diferencia)} /></Col>
    </Row>
  );
}

interface SolicitudesDetalleTableProps {
  detalle?: { loading: boolean; error?: string; solicitudes: SolicitudDetalle[] };
  bloqueado: boolean;
  onReintentar: (fila: SolicitudDetalle) => void;
  isRetrying: (id: string) => boolean;
}

// estado_sat 4/5/6 = Error/Rechazada/Vencida (ver verificar_descarga.py) — rechazo
// terminal del SAT, el id_solicitud viejo ya no sirve. Cualquier otro valor (o
// ausente) es una falla de nuestro lado con la solicitud todavía aceptada en el SAT.
const RECHAZOS_SAT_TERMINALES = new Set(['4', '5', '6']);

const ESTADO_TAG: Record<string, { color: string; label: string }> = {
  pendiente:             { color: 'default',   label: 'Pendiente' },
  en_proceso:            { color: 'processing', label: 'En proceso' },
  procesando:            { color: 'processing', label: 'Procesando' },
  completada:            { color: 'success',    label: 'Completada' },
  completada_sin_datos:  { color: 'success',    label: 'Sin CFDIs en el rango' },
  error:                 { color: 'error',      label: 'Error' },
};

function SolicitudesDetalleTable({ detalle, bloqueado, onReintentar, isRetrying }: SolicitudesDetalleTableProps) {
  if (!detalle || detalle.loading) {
    return (
      <div style={{ padding: '8px 24px' }}>
        <SyncOutlined spin style={{ marginRight: 8 }} />
        <Text type="secondary">Cargando detalle por ventana…</Text>
      </div>
    );
  }
  if (detalle.error) {
    return <Text type="danger" style={{ padding: '8px 24px', display: 'block' }}>{detalle.error}</Text>;
  }

  const columns: ColumnsType<SolicitudDetalle> = [
    {
      title: 'Ventana',
      key: 'ventana',
      render: (_, s) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{s.fecha_inicio} → {s.fecha_fin}</Text>,
    },
    {
      title: 'Tipo',
      key: 'tipo',
      render: (_, s) => <Tag>{s.tipo}</Tag>,
    },
    {
      title: 'Estado',
      key: 'estado',
      render: (_, s) => {
        const info = ESTADO_TAG[s.estado] ?? { color: 'default', label: s.estado };
        return (
          <Space size={4}>
            <Tag color={info.color}>{info.label}</Tag>
            {s.estado === 'completada' && s.parcial && (
              <Tooltip title="Se subieron menos XMLs de los que declaró el SAT — la descarga de esta ventana quedó parcial.">
                <Tag color="warning">Parcial</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Detalle',
      key: 'mensaje',
      width: 320,
      render: (_, s) => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {s.mensaje_error || s.mensaje || (s.numero_cfdis != null ? `${s.numero_cfdis} CFDI(s)` : '—')}
        </div>
      ),
    },
    {
      title: '',
      key: 'acciones',
      width: 110,
      render: (_, s) => {
        const esParcial = s.estado === 'completada' && s.parcial;
        if (s.estado !== 'error' && !esParcial) return null;
        const isRechazoSat = RECHAZOS_SAT_TERMINALES.has(s.estado_sat ?? '');
        const boton = (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            disabled={bloqueado}
            loading={isRetrying(s.id)}
            onClick={() => onReintentar(s)}
          >
            Reintentar
          </Button>
        );
        if (bloqueado) {
          return <Tooltip title="Hay otra ventana en curso para este RFC — espera a que el SAT termine antes de reintentar.">{boton}</Tooltip>;
        }
        const tooltip = esParcial
          ? 'La descarga de esta ventana quedó parcial — se va a pedir de nuevo completa (una solicitud nueva).'
          : isRechazoSat
          ? 'El SAT rechazó esta solicitud — se va a pedir de nuevo.'
          : 'El SAT ya había aceptado esta solicitud — solo se vuelve a verificar, sin gastar una solicitud nueva.';
        return <Tooltip title={tooltip}>{boton}</Tooltip>;
      },
    },
  ];

  return (
    <Table<SolicitudDetalle>
      rowKey="id"
      columns={columns}
      dataSource={detalle.solicitudes}
      pagination={false}
      size="small"
      style={{ margin: '8px 24px' }}
    />
  );
}

interface ReportesTableProps {
  reportes: ReporteListItem[];
  onOpenReglas?: (r: ReporteListItem) => void;
}

function ReportesTable({ reportes, onOpenReglas }: ReportesTableProps) {
  if (!reportes.length) return <Empty description="Sin conciliaciones previas." style={{ padding: 24 }} />;

  const columns: ColumnsType<ReporteListItem> = [
    {
      title: 'RFC',
      key: 'rfc',
      width: 160,
      render: (_, r) => <Tag style={{ fontFamily: 'monospace' }}>{r.rfc}</Tag>,
    },
    {
      title: 'Empresa',
      key: 'empresa',
      ellipsis: true,
      render: (_, r) => <Text type="secondary">{cleanEmpresa(r.nombre_empresa)}</Text>,
    },
    {
      title: 'Período',
      key: 'periodo',
      width: 210,
      render: (_, r) => r.periodo?.inicio
        ? <Text type="secondary" style={{ fontSize: 12 }}>{r.periodo.inicio} → {r.periodo.fin}</Text>
        : '—',
    },
    {
      title: 'Extracto',
      key: 'extracto',
      width: 110,
      align: 'center',
      render: (_, r) => {
        const url = r.movimientos_extraidos_url || r.extracto_urls?.[0];
        return url ? (
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => window.open(url, '_blank')}
          >
            Descargar
          </Button>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
        );
      },
    },
    {
      title: '% Conciliado',
      key: 'pct',
      width: 150,
      render: (_, r) => (
        <div
          style={{ cursor: onOpenReglas ? 'pointer' : 'default' }}
          title={onOpenReglas ? 'Ver reglas de conciliación' : undefined}
          onClick={() => onOpenReglas?.(r)}
        >
          <Progress
            percent={Math.round(r.resumen.porcentaje_conciliado)}
            size="small"
            strokeColor={pctColor(r.resumen.porcentaje_conciliado)}
          />
        </div>
      ),
    },
    {
      title: 'Sin CFDI',
      key: 'sin_cfdi',
      width: 90,
      align: 'center',
      render: (_, r) => r.resumen.sin_cfdi > 0
        ? <Tag color="red">{r.resumen.sin_cfdi}</Tag>
        : <Tag color="green">0</Tag>,
    },
    {
      title: 'Fecha',
      key: 'fecha',
      width: 110,
      render: (_, r) => r.fecha_ejecucion?.slice(0, 10) ?? '—',
    },
  ];

  return (
    <Table<ReporteListItem>
      rowKey={r => r.id ?? r.rfc}
      columns={columns}
      dataSource={reportes}
      pagination={{ pageSize: 8, showSizeChanger: false, size: 'small' }}
      size="small"
      scroll={{ x: 800 }}
    />
  );
}

// ── Utilities ──────────────────────────────────────────────────────

function fileExt(name: string): string {
  return name.split('.').pop()?.toLowerCase() ?? 'xlsx';
}
